import { useId } from 'react'

// Figma Noise export (Duo, size 1×1, density 100%): split the noise field
// into complementary halves so every pixel carries either a dark or a light
// speckle — grain that both darkens and lightens the brand colors.
const DUO_DARK_TABLE = Array.from({ length: 100 }, (_, i) =>
  i < 50 ? '1' : '0',
).join(' ')
const DUO_LIGHT_TABLE = Array.from({ length: 100 }, (_, i) =>
  i < 50 ? '0' : '1',
).join(' ')

// Half the strength of the Figma export (#000/#FFF @ 10% each) — the full
// export read too coarse against the flat brand fields in side-by-side review.
const SPECKLE_ALPHA = 0.05

const SpeckleFilter = ({
  id,
  tableValues,
}: {
  id: string
  tableValues: string
}) => (
  <filter
    id={id}
    x='0'
    y='0'
    width='100%'
    height='100%'
    filterUnits='userSpaceOnUse'
    colorInterpolationFilters='sRGB'
  >
    <feTurbulence
      type='fractalNoise'
      baseFrequency='1.25 1.25'
      stitchTiles='stitch'
      numOctaves='3'
      result='noise'
      seed='3137'
    />
    <feColorMatrix in='noise' type='luminanceToAlpha' result='alphaNoise' />
    <feComponentTransfer in='alphaNoise' result='speckle'>
      <feFuncA type='discrete' tableValues={tableValues} />
    </feComponentTransfer>
    <feComposite operator='in' in='SourceGraphic' in2='speckle' />
  </filter>
)

// Film-grain speckle over the squares. Figma applies a fractal-noise texture
// the MCP export flattens away; this recreates it with the same feTurbulence
// approach as ParchmentBackground. Duo grain per the Figma Noise export:
// complementary dark + light speckle at SPECKLE_ALPHA each.
export const SubjectNoiseOverlay = () => {
  const uid = useId()
  const darkId = `subject-noise-dark-${uid}`
  const lightId = `subject-noise-light-${uid}`
  return (
    <svg
      aria-hidden
      xmlns='http://www.w3.org/2000/svg'
      width='100%'
      height='100%'
      preserveAspectRatio='xMidYMid slice'
      className='pointer-events-none absolute inset-0'
    >
      <defs>
        <SpeckleFilter id={darkId} tableValues={DUO_DARK_TABLE} />
        <SpeckleFilter id={lightId} tableValues={DUO_LIGHT_TABLE} />
      </defs>
      <rect
        width='100%'
        height='100%'
        fill={`rgba(0,0,0,${SPECKLE_ALPHA})`}
        filter={`url(#${darkId})`}
      />
      <rect
        width='100%'
        height='100%'
        fill={`rgba(255,255,255,${SPECKLE_ALPHA})`}
        filter={`url(#${lightId})`}
      />
    </svg>
  )
}

// 3-layer concentric brand mark + film grain, drawn to fill its parent box
// (no aspect-ratio of its own). Used as the static visual for a subject square
// and as the fallback layer behind a subject Lottie animation while it loads,
// when it fails, or when the user prefers reduced motion.
export const SubjectSquareLayers = ({
  showNoise = true,
}: { showNoise?: boolean } = {}) => (
  <>
    <div className='absolute inset-0 bg-edu-subject-square-outer' />
    <div className='absolute inset-[20%] bg-edu-subject-square-mid' />
    <div className='absolute inset-[40%] bg-edu-subject-square-inner' />
    {showNoise && <SubjectNoiseOverlay />}
  </>
)
